import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { BRAND } from '../brand';
import ImplantPopup from '../components/ImplantPopup';
import AddImplantModal from '../components/AddImplantModal';
import StockListCard from '../components/StockListCard';
import StockMobileList from '../components/StockMobileList';
import StockPageLayout from '../components/StockPageLayout';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import StockAddHeaderButton from '../components/StockAddHeaderButton';
import useIsMobile from '../hooks/useIsMobile';
import { encodeRouteParam } from '../util/routeParams';

const SORT_OPTIONS = {
  implant_name: { label: 'Implant Name', param: 'implant_name' },
  sizes: { label: 'Sizes', param: 'sizes' },
  vendor_name: { label: 'Vendor', param: 'vendor_name' },
  total_quantity: { label: 'Total Quantity', param: 'total_quantity' },
  earliest_expiry: { label: 'Days to Expire', param: 'earliest_expiry' },
  created_by: { label: 'Created By', param: 'created_by' }
};

const Implants = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [implants, setImplants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImplant, setSelectedImplant] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('implant_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);

  const fetchImplants = useCallback(async () => {
    try {
      setLoading(true);
      const params = { sortBy, sortOrder };
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/implants', { params });
      setImplants(response.data.implants || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch implants');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchImplants();
  }, [fetchImplants]);

  const handleImplantClick = (implantName) => {
    if (isMobile) {
      navigate(`/implants/${encodeRouteParam(implantName)}`);
      return;
    }
    setSelectedImplant(implantName);
  };

  const handleClosePopup = () => {
    setSelectedImplant(null);
    fetchImplants();
  };

  const headerAction = useMemo(
    () => (
      <StockAddHeaderButton
        label="Add implant"
        onClick={() => setAddModalOpen(true)}
      />
    ),
    []
  );

  useSetPageHeaderAction(headerAction);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const SortableHeader = ({ column, children, style = {} }) => (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => handleSort(column)}
      title={`Sort by ${SORT_OPTIONS[column]?.label || column}`}
    >
      {children}
      {sortBy === column && (
        <span style={{ marginLeft: 4, opacity: 0.9 }}>
          {sortOrder === 'asc' ? ' ▲' : ' ▼'}
        </span>
      )}
    </th>
  );

  const getDaysToExpire = (expiryDate, asOfDate) => {
    if (!expiryDate) return 'N/A';
    const expiry = new Date(expiryDate);
    if (Number.isNaN(expiry.getTime())) return 'N/A';
    const ref = asOfDate ? new Date(asOfDate) : new Date();
    const ms = expiry.getTime() - ref.getTime();
    const days = Math.round(ms / (24 * 60 * 60 * 1000));
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
    return 'Expires today';
  };

  const totalPages = Math.ceil(implants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedImplants = implants.slice(startIndex, startIndex + itemsPerPage);

  const paginationBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography level="body-md">
        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, implants.length)} of {implants.length} implants
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(currentPage + 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
    <StockPageLayout
      error={error && <Alert color="danger">{error}</Alert>}
      loading={loading}
      search={(
        <Card sx={{ mb: { xs: 1.5, md: 3 }, p: 2 }}>
          <FormControl sx={{ maxWidth: 400 }}>
            <FormLabel>Search by Implant Name</FormLabel>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search implants..."
              sx={{
                '&:hover': { borderColor: 'primary.500' },
                '&:focus-within': { borderColor: 'primary.500' }
              }}
            />
          </FormControl>
        </Card>
      )}
      pagination={paginationBar}
    >
          <StockMobileList empty={paginatedImplants.length === 0} emptyMessage="No implants found">
            {paginatedImplants.map((implant) => (
              <StockListCard
                key={implant.implant_name}
                title={implant.implant_name}
                onClick={() => handleImplantClick(implant.implant_name)}
                fields={[
                  { label: 'Size(s)', value: implant.sizes || '—' },
                  { label: 'Vendor', value: implant.vendor_name || 'N/A' },
                  { label: 'Total Quantity', value: implant.total_quantity },
                  { label: 'Days to Expire', value: getDaysToExpire(implant.earliest_expiry) },
                  { label: 'Created By', value: implant.created_by || '—' }
                ]}
              />
            ))}
          </StockMobileList>

          <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: 'primary.500',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: 'primary.50',
                  cursor: 'pointer'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <SortableHeader column="implant_name" style={{ width: '22%' }}>
                    Implant Name
                  </SortableHeader>
                  <SortableHeader column="sizes" style={{ width: '14%' }}>
                    Size(s)
                  </SortableHeader>
                  <SortableHeader column="vendor_name" style={{ width: '16%' }}>
                    Vendor
                  </SortableHeader>
                  <SortableHeader column="total_quantity" style={{ width: '12%' }}>
                    Total Quantity
                  </SortableHeader>
                  <SortableHeader column="earliest_expiry" style={{ width: '12%' }}>
                    Days to Expire
                  </SortableHeader>
                  <SortableHeader column="created_by" style={{ width: '18%' }}>
                    Created By
                  </SortableHeader>
                </tr>
              </thead>
              <tbody>
                {paginatedImplants.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                      No implants found
                    </td>
                  </tr>
                ) : (
                  paginatedImplants.map((implant) => (
                    <tr
                      key={implant.implant_name}
                      onClick={() => handleImplantClick(implant.implant_name)}
                    >
                      <td style={{ color: BRAND.colors.navy, fontWeight: 'bold' }}>
                        {implant.implant_name}
                      </td>
                      <td>{implant.sizes || '—'}</td>
                      <td>{implant.vendor_name || 'N/A'}</td>
                      <td>{implant.total_quantity}</td>
                      <td>{getDaysToExpire(implant.earliest_expiry)}</td>
                      <td>{implant.created_by || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
    </StockPageLayout>

      {selectedImplant && (
        <ImplantPopup
          implantName={selectedImplant}
          onClose={handleClosePopup}
        />
      )}

      <AddImplantModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={fetchImplants}
      />
    </>
  );
};

export default Implants;
